'use client';

/**
 * HandoffTimeoutPanel — the "no agent available" recovery flow shown while
 * HandoffPhase.TIMED_OUT (and kept mounted through the ENDED/
 * timeout_no_agent tail so this moment stays owned by THIS panel rather
 * than the generic "You're back with Sara" recap — see page.tsx's
 * `isHandoffTimedOut` gate and handoff-recap-card.tsx's `endReason` guard).
 *
 * Single container owning the small view machine (options → message |
 * callback → submitted — see lib/handoff-timeout-view.ts for the pure,
 * unit-tested transition table) plus the one live API call
 * (lib/api/support-requests.ts). Each sub-view owns its OWN raw form-field
 * state (textarea value / phone+window) so a failed submit leaves the
 * user's typed content on screen untouched: the view stays mounted, only
 * `error` changes — see handoff-timeout-message.tsx / -callback.tsx's own
 * doc comments.
 *
 * Renders its own "No guide could join in time" lead-in chip (via
 * HandoffStatusChip) directly above the active card — mirrors
 * HandoffRecapCard's own hardcoded chip+card pairing, except there's no
 * backend row to claim/supersede here: this panel is the sole owner of the
 * TIMED_OUT moment, not a restyle of an existing SYSTEM_MESSAGE.
 */
import { useState } from 'react';
import {
  HandoffTimeoutAction,
  HandoffTimeoutView,
  nextTimeoutView,
} from '@/lib/handoff-timeout-view';
import {
  submitSupportRequest,
  SupportRequestType,
  type CallbackWindow,
} from '@/lib/api/support-requests';
import {
  HANDOFF_STATUS_CHIP_KIND,
  HandoffStatusChip,
} from './handoff-status-chip';
import { HandoffTimeoutOptions } from './handoff-timeout-options';
import { HandoffTimeoutMessage } from './handoff-timeout-message';
import { HandoffTimeoutCallback } from './handoff-timeout-callback';
import { HandoffTimeoutSubmitted } from './handoff-timeout-submitted';

interface HandoffTimeoutPanelProps {
  handoffId: string;
  conversationId: string;
  /** Resolved by the caller from the CONTACT_SUMMARY message tied to this
   *  handoff (see page.tsx) — undefined only if that row can't be found. */
  email?: string;
  phoneOnFile?: string;
}

const GENERIC_ERROR = 'Something went wrong. Please try again.';

export function HandoffTimeoutPanel({
  handoffId,
  conversationId,
  email,
  phoneOnFile,
}: HandoffTimeoutPanelProps) {
  const [view, setView] = useState<HandoffTimeoutView>(
    HandoffTimeoutView.OPTIONS
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedType, setSubmittedType] = useState<SupportRequestType | null>(
    null
  );

  const goTo = (action: HandoffTimeoutAction) => {
    setError(null);
    setView(nextTimeoutView(action));
  };

  const handleSubmitMessage = async (body: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await submitSupportRequest(conversationId, {
        type: SupportRequestType.MESSAGE,
        handoff_id: handoffId,
        body,
      });
      setSubmittedType(SupportRequestType.MESSAGE);
      goTo(HandoffTimeoutAction.SUBMITTED);
    } catch (err) {
      setError(err instanceof Error ? err.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCallback = async (
    phone: string,
    callbackWindow: CallbackWindow
  ) => {
    setSubmitting(true);
    setError(null);
    try {
      await submitSupportRequest(conversationId, {
        type: SupportRequestType.CALLBACK,
        handoff_id: handoffId,
        phone,
        callback_window: callbackWindow,
      });
      setSubmittedType(SupportRequestType.CALLBACK);
      goTo(HandoffTimeoutAction.SUBMITTED);
    } catch (err) {
      setError(err instanceof Error ? err.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-[12px]">
      {/* Live-only mount (never a restored/historical row — see this
          file's header comment), so the entrance animation always plays;
          honors prefers-reduced-motion internally. */}
      <HandoffStatusChip kind={HANDOFF_STATUS_CHIP_KIND.TIMED_OUT} animateIn />

      <div className="w-full">
        {view === HandoffTimeoutView.OPTIONS && (
          <HandoffTimeoutOptions
            onChooseMessage={() => goTo(HandoffTimeoutAction.CHOOSE_MESSAGE)}
            onChooseCallback={() => goTo(HandoffTimeoutAction.CHOOSE_CALLBACK)}
          />
        )}
        {view === HandoffTimeoutView.MESSAGE && (
          <HandoffTimeoutMessage
            email={email}
            onBack={() => goTo(HandoffTimeoutAction.BACK)}
            onSubmit={handleSubmitMessage}
            submitting={submitting}
            error={error}
          />
        )}
        {view === HandoffTimeoutView.CALLBACK && (
          <HandoffTimeoutCallback
            phoneOnFile={phoneOnFile}
            onBack={() => goTo(HandoffTimeoutAction.BACK)}
            onSubmit={handleSubmitCallback}
            submitting={submitting}
            error={error}
          />
        )}
        {view === HandoffTimeoutView.SUBMITTED && submittedType && (
          <HandoffTimeoutSubmitted type={submittedType} />
        )}
      </div>
    </div>
  );
}
